import { useState, useMemo } from "react";
import { motion } from "framer-motion";

const TAXI_TYPES = [
  "Toyota Corolla",
  "Toyota Camry",
  "Toyota Avensis",
  "Honda Civic",
  "Hyundai Elantra",
  "Nissan Almera",
  "Bolt/Uber Vehicle",
  "Other"
];

export default function TaxiForm({ onSubmit, onBack, initialData = {} }) {
  const [licensePlate, setLicensePlate] = useState(initialData.licensePlate || "");
  const [taxiType, setTaxiType] = useState(initialData.taxiType || "");
  const [customTaxiType, setCustomTaxiType] = useState(initialData.customTaxiType || "");

  const isValid = useMemo(() => {
    return licensePlate.trim() && taxiType;
  }, [licensePlate, taxiType]);

  const handleSubmit = () => {
    const data = {
      licensePlate: licensePlate.trim(),
      taxiType: taxiType === "Other" ? customTaxiType : taxiType,
    };
    onSubmit(data);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="space-y-6"
    >
      <div className="text-center mb-6">
        <h3 className="text-xl font-bold text-gray-900 mb-2">Taxi/Bolt Details</h3>
        <p className="text-sm text-gray-600">Please provide your taxi or Bolt vehicle information</p>
      </div>

      {/* License Plate */}
      <div>
        <label className="block mb-2 text-sm font-semibold text-gray-800">
          License Plate Number *
        </label>
        <input
          type="text"
          placeholder="e.g., GR-1234-21"
          value={licensePlate}
          onChange={(e) => setLicensePlate(e.target.value.toUpperCase())}
          className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 outline-none"
        />
        <p className="mt-1 text-xs text-gray-500">
          Enter your taxi or Bolt vehicle's license plate number
        </p>
      </div>

      {/* Taxi Type */}
      <div>
        <label className="block mb-2 text-sm font-semibold text-gray-800">
          Vehicle Type/Model *
        </label>
        <div className="grid grid-cols-2 gap-2">
          {TAXI_TYPES.map((type) => (
            <label key={type} className="cursor-pointer">
              <input
                type="radio"
                className="sr-only peer"
                name="taxiType"
                value={type}
                checked={taxiType === type}
                onChange={() => setTaxiType(type)}
              />
              <div className="p-3 text-sm font-medium text-center rounded-xl border border-gray-200 peer-checked:border-emerald-400 peer-checked:ring-2 peer-checked:ring-emerald-100 hover:border-emerald-200">
                {type}
              </div>
            </label>
          ))}
        </div>
        
        {taxiType === "Other" && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-3"
          >
            <input
              type="text"
              placeholder="Specify vehicle type/model"
              value={customTaxiType}
              onChange={(e) => setCustomTaxiType(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 outline-none"
            />
          </motion.div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 pt-4">
        <button
          onClick={onBack}
          className="flex-1 px-4 py-3 font-semibold text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
        >
          Back
        </button>
        <button
          onClick={handleSubmit}
          disabled={!isValid}
          className="flex-1 px-4 py-3 font-semibold text-white bg-emerald-600 rounded-xl hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          Continue
        </button>
      </div>
    </motion.div>
  );
}
